import * as React from 'react';
import type { ImageStyle, StyleProp, TextStyle, ViewStyle } from 'react-native';

import { FocusRingContext } from '../FocusRing';
import type { PropsOf } from '../propsOf';
import { ThemedStyleSheet, useCurrentTheme } from '../theme';
import { convertStyleToCSS, hashStyle, insertCSSStyle } from '../util';
import type { InteractiveHandle } from './handles';
import { createInteractiveHandle, InteractiveHandlesContext } from './handles';

export type InteractiveViewStyle =
    | StyleProp<ViewStyle | TextStyle | ImageStyle>
    | 'hidden'
    | 'visible';

export interface InteractiveViewOwnProps {
    children?: React.ReactNode;
    /* Identify this element using a handle */
    handle?: InteractiveHandle;
    /** To which element to refer the interactive state */
    descendantOf?: InteractiveHandle;
    /** Style applied in all cases */
    style?: InteractiveViewStyle;
    /** Style applied only when the view is not hovered nor focused */
    inactiveStyle?: InteractiveViewStyle;
    /** Style applied only when the view is hovered */
    hoverStyle?: InteractiveViewStyle;
    /** Style applied only when the view is focused */
    focusStyle?: InteractiveViewStyle;
    /** Style applied when the view is focused or hovered */
    activeStyle?: InteractiveViewStyle;
    /** Enable focus ring to make focus visible for keyboard users */
    withFocusRing?: boolean;
    /** DOM dataSet prop */
    dataSet?: Record<string, string>;
}

export type InteractiveViewProps<E extends React.ElementType = React.ElementType> =
    InteractiveViewOwnProps & Omit<PropsOf<E>, keyof InteractiveViewOwnProps>;

export type InteractiveComponent<E extends React.ElementType> = ((
    props: InteractiveViewProps<E>
) => React.ReactElement) & {
    isInteractive: true;
    /** Utility to base style on interactive state of more direct interactive ancestor */
    Child: (props: InteractiveViewProps<E>) => React.ReactElement;
};

const INTERACTIVE_HANDLE_KEY = 'rnwi-handle';
const INTERACTIVE_HANDLE_ATTRIBUTE = `data-${INTERACTIVE_HANDLE_KEY}`;

/**
 * Default handle when using Interactive.Child without a handle created with createInteractiveHandle.
 * If you use a double nesting of Interactive.View without a handle, it'll throw an error in development.
 */
const fallbackHandle = createInteractiveHandle('nearest');

const cache = new WeakMap<React.ComponentType, InteractiveComponent<any>>();

/**
 * Version of createInteractiveComponent for the web build.
 */
export function createInteractiveComponent<E extends React.ComponentType>(
    Component: E
): InteractiveComponent<E> {
    const fromCache = cache.get(Component);
    if (fromCache) {
        return fromCache;
    }

    // @ts-ignore
    const InteractiveComponent: InteractiveComponent<E> = React.forwardRef(
        function InteractiveComponentWithRef<E extends React.ElementType>(
            props: InteractiveViewProps<E>,
            ref
        ): React.ReactElement {
            const {
                handle,
                descendantOf,
                children,
                style: baseStyle,
                inactiveStyle,
                hoverStyle,
                focusStyle,
                activeStyle,
                dataSet,
                withFocusRing,
                ...rest
            } = props;

            const context = React.useContext(InteractiveHandlesContext);
            const focusRingContext = React.useContext(FocusRingContext);
            const focusRingIsEnabled =
                withFocusRing !== undefined ? withFocusRing : focusRingContext.enabled;
            const handleKey = handle || fallbackHandle;
            const [, { focusRingStyle }] = useCurrentTheme(themedStyles);

            if (process.env.NODE_ENV === 'development') {
                if (descendantOf) {
                    const count = context.stack.filter((item) => item === descendantOf).length;
                    if (count > 1) {
                        throw new Error(
                            `descendantOf ("${descendantOf}") passed to Interactive.View match multiple ancestors. It can cause unexpected hover/active effects.\n\nMake sure to use createInteractiveHandle and pass it as a prop to both components (<Interactive.View handle={handle}> and <Interactive.View descendantOf={handle}>).`
                        );
                    }
                }
            }

            const parentHandle = descendantOf ? context.handles[descendantOf] : null;

            // When using an interactive with a non existing `descendantOf`, we disable the interactive
            // effect and act as a regular View.
            const enabled = descendantOf ? !!parentHandle : true;

            const childrenContext = React.useMemo(() => {
                if (!enabled) {
                    return context;
                }

                return {
                    stack: [...context.stack, handleKey],
                    nearestHandle: handleKey,
                    handles: { ...context.handles, [handleKey]: handleKey },
                };
            }, [context, handleKey, enabled]);

            const classDataSet = React.useMemo(() => {
                return enabled
                    ? flushInteractiveStyles(
                          {
                              inactiveStyle,
                              hoverStyle,
                              focusStyle,
                              activeStyle,
                              // @ts-ignore
                              focusRingStyle: focusRingIsEnabled ? focusRingStyle : undefined,
                          },
                          parentHandle
                      )
                    : {};
            }, [
                parentHandle,
                inactiveStyle,
                hoverStyle,
                focusStyle,
                activeStyle,
                focusRingIsEnabled,
                focusRingStyle,
            ]);

            return (
                // @ts-ignore
                <Component
                    {...rest}
                    ref={ref}
                    style={baseStyle}
                    // @ts-ignore dataSet is RNW specific. we can't use className directly because RNW overrides it with its own
                    dataSet={{ ...dataSet, ...classDataSet, [INTERACTIVE_HANDLE_KEY]: handleKey }}
                >
                    <InteractiveHandlesContext.Provider value={childrenContext}>
                        {children}
                    </InteractiveHandlesContext.Provider>
                </Component>
            );
        }
    );

    // @ts-ignore
    InteractiveComponent.Child = React.forwardRef(function InteractiveComponentWithRef(
        props: InteractiveViewProps<E>,
        ref
    ): React.ReactElement {
        const { nearestHandle } = React.useContext(InteractiveHandlesContext);
        if (!nearestHandle && !props.descendantOf) {
            throw new Error(
                `${
                    // @ts-ignore
                    InteractiveComponent.Child.displayName || 'InteractiveChildComponent'
                } should be a descendant of an interactive component`
            );
        }

        const hasParentFocusRing = Boolean(nearestHandle) || Boolean(props.descendantOf);

        return (
            <InteractiveComponent
                ref={ref}
                descendantOf={nearestHandle}
                {...props}
                withFocusRing={hasParentFocusRing ? false : props.withFocusRing}
            />
        );
    });

    InteractiveComponent.isInteractive = true;

    if ('displayName' in Component || 'name' in Component) {
        // @ts-ignore
        InteractiveComponent.displayName = `Interactive(${
            // @ts-ignore
            Component.displayName || Component.name
        })`;
        // @ts-ignore
        InteractiveComponent.Child.displayName = `InteractiveChild(${
            // @ts-ignore
            Component.displayName || Component.name
        })`;
    }

    cache.set(Component, InteractiveComponent);

    return InteractiveComponent;
}

// we use a css group of 20 not to mess up with the existing ones
const cssGroup = 20;

/**
 * Flush the css and allocate the data-set to add to the element.
 */
function flushInteractiveStyles(
    styles: {
        inactiveStyle: InteractiveViewStyle | undefined;
        hoverStyle: InteractiveViewStyle | undefined;
        focusStyle: InteractiveViewStyle | undefined;
        activeStyle: InteractiveViewStyle | undefined;
        focusRingStyle: InteractiveViewStyle | undefined;
    },
    relativeToParentHandle: string | null
): Record<string, boolean> {
    const data: Record<string, boolean> = {};

    if (styles.inactiveStyle) {
        flushStyleWithId(data, [null], styles.inactiveStyle, relativeToParentHandle);
    }
    if (styles.hoverStyle) {
        flushStyleWithId(data, ['hover'], styles.hoverStyle, relativeToParentHandle);
    }
    if (styles.focusStyle) {
        flushStyleWithId(data, ['focus'], styles.focusStyle, relativeToParentHandle);
    }
    if (styles.activeStyle) {
        flushStyleWithId(data, ['hover', 'focus'], styles.activeStyle, relativeToParentHandle);
    }
    if (styles.focusRingStyle) {
        flushStyleWithId(data, ['focus-visible'], styles.focusRingStyle, relativeToParentHandle);
    }

    return data;
}

function flushStyleWithId(
    data: Record<string, boolean>,
    pseudoClasses: Array<string | null>,
    style: InteractiveViewStyle,
    relativeToParentHandle: string | null
): void {
    const hash = hashStyle(style);
    if (!hash) {
        // No style to flush
        return;
    }

    const [id, newId] = getId(`${relativeToParentHandle || ''}-${hash}-${pseudoClasses.join('-')}`);
    data[getDataKey(id)] = true;

    if (!newId) {
        // We already flushed this style
        return;
    }

    const parentSelector = relativeToParentHandle
        ? `[${INTERACTIVE_HANDLE_ATTRIBUTE}="${relativeToParentHandle}"]`
        : '';
    const elementSelector = `[${getDataAttribute(id)}]`;

    const interactiveSelector = parentSelector ? parentSelector : elementSelector;
    const childSelector = parentSelector ? ` ${elementSelector}` : '';

    const css = convertStyleToCSS(style);
    const selectors = pseudoClasses
        .map((pseudoClass) =>
            pseudoClass
                ? `body:not(.dragging) ${interactiveSelector}:${pseudoClass}${childSelector}`
                : `${interactiveSelector}${childSelector}`
        )
        .join(', ');

    insertCSSStyle(`${selectors}{${css}}`, cssGroup);
}

const ids = new Set<string>();

function getId(hash: string): [string, boolean] {
    const id = hash.toLowerCase();

    if (typeof window === 'undefined') {
        return [id, true];
    }

    if (ids.has(id)) {
        return [id, false];
    }

    ids.add(id);
    return [id, true];
}

function getDataKey(id: string): string {
    return `rnwi${id}`;
}

function getDataAttribute(id: string): string {
    return `data-${getDataKey(id)}`;
}

const themedStyles = ThemedStyleSheet.create({
    focusRingStyle: (theme) => ({
        outlineWidth: 2,
        outlineStyle: 'auto',
        outlineColor: theme.colors.standout.standout,
        outlineOffset: 0,
    }),
});
